import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { api, type ParsedRfqResponse } from '@/lib/api';
import { useModalDismiss } from '@/lib/use-modal-dismiss';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ParseEmailModal({ open, onClose }: Props) {
  const dismiss = useModalDismiss(onClose);
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  const [rawText, setRawText] = useState('');

  const parseMutation = useMutation({
    mutationFn: async (text: string) => {
      return api<ParsedRfqResponse>('/api/rfqs/parse-email', {
        method: 'POST',
        tokenGetter: () => getToken(),
        idempotencyKey: crypto.randomUUID(),
        body: JSON.stringify({ rawText: text, sourceChannel: 'manual' }),
      });
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['rfqs'] });
      setRawText('');
      onClose();
      navigate(`/rfqs/${data.rfq.id}`);
    },
  });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      {...dismiss}
    >
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-2xl max-h-[90dvh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="font-semibold">Parse customer RFQ email</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Paste the email body. Claude extracts a structured RFQ committed under your tenant.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-sm"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="p-5 flex-1 overflow-auto">
          <textarea
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            placeholder="Hi Austin,&#10;&#10;We need pricing on the following:&#10;&#10;1) PN 285W0024-1M, qty 2, NE preferred, A320&#10;2) PN 112005-101, qty 1, OH ok, AS350 IBF kit&#10;&#10;AOG, please rush.&#10;&#10;Sara at ABC Aerospace&#10;sara@abc-aerospace.com"
            className="w-full h-72 px-3 py-2 text-sm font-mono bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40"
            autoFocus
          />
          {parseMutation.isError && (
            <div className="mt-3 text-sm text-destructive">
              {(parseMutation.error as { message?: string })?.message ?? 'Parse failed'}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-md text-muted-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            onClick={() => parseMutation.mutate(rawText)}
            disabled={rawText.trim().length < 20 || parseMutation.isPending}
            className="px-4 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
          >
            {parseMutation.isPending ? 'Parsing…' : 'Parse'}
          </button>
        </div>
      </div>
    </div>
  );
}
